/*
 * TEFCO — product page controller (Step 7).
 * Progressive enhancement over a native <form action="/cart/add">.
 * Handles: variant selection (radio pills) -> price / SKU / stock / image / URL,
 * quantity stepper, thumbnail swap, AJAX add-to-cart, quote link pre-fill.
 * Works only as an enhancement; with JS off the <noscript> <select name="id">
 * and the native form submit still function.
 */
(function () {
  'use strict';

  class TefcoProduct extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this.sectionId = this.dataset.sectionId;
      this.productUrl = (this.dataset.url || '').split('?')[0];
      this.map = this.readMap();

      this.form = this.querySelector('[data-tefco-product-form]');
      this.idInput = this.querySelector('[data-tefco-variant-id]');
      this.optionInputs = Array.from(this.querySelectorAll('[data-tefco-option-input]'));
      this.priceWrap = this.querySelector('[data-tefco-price]');
      // Price markup comes from the shared tier-aware snippets/tefco-price.liquid
      this.priceNow = this.querySelector('.tefco-price__now');
      this.priceWas = this.querySelector('.tefco-price__was');
      this.priceOff = this.querySelector('.tefco-price__off');
      this.stockEl = this.querySelector('[data-tefco-stock]');
      this.skuEl = this.querySelector('[data-tefco-sku]');
      this.atcBtn = this.querySelector('[data-tefco-atc]');
      this.atcText = this.querySelector('[data-tefco-atc-text]');
      this.qtyInput = this.querySelector('[data-tefco-qty-input]');
      this.mainImg = this.querySelector('.tefco-pp-gallery__img');
      this.thumbs = Array.from(this.querySelectorAll('[data-tefco-thumb]'));
      this.quote = this.querySelector('[data-tefco-quote]');
      this.quoteBase = this.quote ? this.quote.getAttribute('href').split('?')[0] : '';

      this.bindOptions();
      this.bindQty();
      this.bindThumbs();
      this.bindZoom();
      this.bindLightbox();
      this.bindAtc();
      this.syncQuote();
    }

    readMap() {
      var el = this.querySelector('[data-tefco-variant-map]');
      if (!el) return {};
      try { return JSON.parse(el.textContent); } catch (e) { return {}; }
    }

    /* ---------- variant selection ---------- */
    bindOptions() {
      if (!this.optionInputs.length) return;
      this.addEventListener('change', function (e) {
        if (!e.target.matches('[data-tefco-option-input]')) return;
        this.onOptionChange();
      }.bind(this));
    }

    selectedOptions() {
      // one selected radio per option group, in option order
      var groups = {};
      this.optionInputs.forEach(function (input) {
        if (input.checked) groups[input.name] = input.value;
      });
      return Object.keys(groups).sort().map(function (k) { return groups[k]; });
    }

    findVariantId(selected) {
      var ids = Object.keys(this.map);
      for (var i = 0; i < ids.length; i++) {
        var opts = this.map[ids[i]].options || [];
        if (opts.length === selected.length && opts.every(function (v, idx) { return v === selected[idx]; })) {
          return ids[i];
        }
      }
      return null;
    }

    onOptionChange() {
      // reflect active pill state
      this.optionInputs.forEach(function (input) {
        var pill = input.closest('.tefco-pp-pill');
        if (pill) pill.classList.toggle('is-active', input.checked);
      });

      var id = this.findVariantId(this.selectedOptions());
      if (!id) { this.setUnavailable(); return; }
      var data = this.map[id];

      if (this.idInput) this.idInput.value = id;
      if (this.priceNow) this.priceNow.textContent = data.price_f;
      if (this.priceWas) {
        if (data.on_sale && data.compare_f) { this.priceWas.textContent = data.compare_f; this.priceWas.hidden = false; this.priceWas.style.display = ''; }
        else { this.priceWas.hidden = true; this.priceWas.style.display = 'none'; }
      }
      if (this.skuEl && data.sku) {
        // Rebuild with <bdi> so the Latin SKU stays LTR-isolated on Arabic pages (§7).
        this.skuEl.textContent = 'SKU: ';
        var bdi = document.createElement('bdi');
        bdi.className = 'tefco-latin';
        bdi.textContent = data.sku;
        this.skuEl.appendChild(bdi);
      }
      this.setStock(data.available);
      if (data.img && this.mainImg) this.swapMainImage(data.img);

      // reflect ?variant= in the URL without reloading
      try {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', id);
        window.history.replaceState({}, '', url.toString());
      } catch (e) {}

      this.syncQuote();
    }

    setStock(available) {
      if (this.stockEl) {
        this.stockEl.textContent = available ? 'In stock' : 'Out of stock';
        this.stockEl.classList.toggle('tefco-pp-stock--in', available);
        this.stockEl.classList.toggle('tefco-pp-stock--out', !available);
      }
      if (this.atcBtn) {
        this.atcBtn.disabled = !available;
        if (this.atcText) this.atcText.textContent = available ? (this.atcBtn.dataset.labelDefault || 'Add to Cart') : 'Sold out';
      }
    }

    setUnavailable() {
      if (this.atcBtn) { this.atcBtn.disabled = true; if (this.atcText) this.atcText.textContent = 'Unavailable'; }
      if (this.stockEl) { this.stockEl.textContent = 'Unavailable'; this.stockEl.classList.remove('tefco-pp-stock--in'); this.stockEl.classList.add('tefco-pp-stock--out'); }
    }

    swapMainImage(src) {
      // normalize to a width param already present in srcset sizing
      var clean = src.split('?')[0];
      this.mainImg.src = clean + (clean.indexOf('?') === -1 ? '?' : '&') + 'width=1200';
      this.mainImg.removeAttribute('srcset');
      this.thumbs.forEach(function (t) {
        var isMatch = (t.dataset.full || '').split('?')[0] === clean;
        t.classList.toggle('is-active', isMatch);
        t.setAttribute('aria-pressed', isMatch ? 'true' : 'false');
      });
    }

    /* ---------- quantity ---------- */
    bindQty() {
      var self = this;
      this.querySelectorAll('[data-qty-step]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          if (!self.qtyInput) return;
          var step = parseInt(btn.getAttribute('data-qty-step'), 10) || 0;
          var val = (parseInt(self.qtyInput.value, 10) || 1) + step;
          if (val < 1) val = 1;
          self.qtyInput.value = val;
          self.syncQuote();
        });
      });
      if (this.qtyInput) this.qtyInput.addEventListener('change', function () {
        if ((parseInt(self.qtyInput.value, 10) || 0) < 1) self.qtyInput.value = 1;
        self.syncQuote();
      });
    }

    /* ---------- thumbnails ---------- */
    /* Promote one thumbnail to the main image. Shared by click, hover and focus
       so all three routes behave identically. */
    showThumb(btn) {
      var full = btn.dataset.full;
      if (!full || !this.mainImg) return;
      if (this.mainImg.getAttribute('src') === full) return;   // already showing
      this.mainImg.src = full;
      this.mainImg.removeAttribute('srcset');   // srcset would override the new src
      this.thumbs.forEach(function (t) {
        t.classList.remove('is-active');
        t.setAttribute('aria-pressed', 'false');
      });
      btn.classList.add('is-active');
      btn.setAttribute('aria-pressed', 'true');
    }

    bindThumbs() {
      var self = this;
      // Hovering a thumbnail swaps it into the frame with no click, which is what
      // shoppers expect from a hardware catalogue. Gated on a real hover-capable
      // pointer so a tap on mobile does not fire a phantom hover before the click.
      var canHover = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

      this.thumbs.forEach(function (btn) {
        btn.addEventListener('click', function () { self.showThumb(btn); });
        // Keyboard parity: tabbing through the thumbnails previews them too.
        btn.addEventListener('focus', function () { self.showThumb(btn); });
        if (canHover) {
          btn.addEventListener('mouseenter', function () { self.showThumb(btn); });
        }
      });
    }

    /* ---------- hover-to-magnify on the main image ----------
       The frame already clips its contents, so magnifying is just a scale on the
       image plus a transform-origin that tracks the cursor — no second overlay
       image, no library, and nothing extra to download. Pointer-fine only: on a
       touch screen there is no cursor to follow, and a stuck 2.2x image would be
       a trap. */
    bindZoom() {
      var frame = this.querySelector('.tefco-pp-gallery__frame');
      var img = this.mainImg;
      if (!frame || !img) return;
      if (!(window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches)) return;

      var ZOOM = 2.2;
      frame.classList.add('is-zoomable');

      frame.addEventListener('mouseenter', function () {
        img.style.transform = 'scale(' + ZOOM + ')';
      });
      frame.addEventListener('mousemove', function (e) {
        var r = frame.getBoundingClientRect();
        var x = ((e.clientX - r.left) / r.width) * 100;
        var y = ((e.clientY - r.top) / r.height) * 100;
        img.style.transformOrigin = x + '% ' + y + '%';
      });
      frame.addEventListener('mouseleave', function () {
        img.style.transform = '';
        img.style.transformOrigin = '';
      });
    }

    /* ---------- click the main image to see it full size ----------
       Bound on the frame, not gated on pointer type: on touch, where the
       hover-magnify is switched off, tapping to enlarge is the ONLY way to see
       detail, so it has to work there most of all. */
    bindLightbox() {
      var self = this;
      var frame = this.querySelector('.tefco-pp-gallery__frame');
      var box = this.querySelector('[data-tefco-lightbox]');
      var boxImg = this.querySelector('[data-tefco-lightbox-img]');
      var prev = this.querySelector('[data-tefco-lb-prev]');
      var next = this.querySelector('[data-tefco-lb-next]');
      var count = this.querySelector('[data-tefco-lb-count]');
      var img = this.mainImg;
      if (!frame || !box || !boxImg || !img) return;

      // Every image of the product, in gallery order. Thumbnails only render when
      // there is more than one, so a single-image product falls back to the main
      // image and the arrows stay hidden.
      var srcs = this.thumbs.map(function (t) { return t.dataset.full; }).filter(Boolean);
      if (!srcs.length) srcs = [img.currentSrc || img.src].filter(Boolean);
      var i = 0;

      // Ask Shopify's CDN for a bigger rendition than the 1200px the frame uses.
      function big(u) { return u.replace(/([?&]width=)\d+/, '$11800'); }

      function show(n) {
        if (!srcs.length) return;
        i = (n + srcs.length) % srcs.length;         // wraps both ways
        boxImg.src = big(srcs[i]);
        if (count) count.textContent = srcs.length > 1 ? (i + 1) + ' / ' + srcs.length : '';
      }

      var many = srcs.length > 1;
      if (prev) prev.hidden = !many;
      if (next) next.hidden = !many;

      frame.addEventListener('click', function () {
        var current = (img.currentSrc || img.src || '').split('?')[0];
        var at = srcs.findIndex(function (u) { return u.split('?')[0] === current; });
        show(at > -1 ? at : 0);
        boxImg.alt = img.alt || '';
        if (window.TEFCO && TEFCO.openOverlay) TEFCO.openOverlay(box);
      });

      if (prev) prev.addEventListener('click', function () { show(i - 1); });
      if (next) next.addEventListener('click', function () { show(i + 1); });

      // Arrow keys, but only while this lightbox is the open overlay — otherwise
      // they would hijack arrow keys for the whole page.
      document.addEventListener('keydown', function (e) {
        if (box.getAttribute('data-open') !== 'true' || !many) return;
        if (e.key === 'ArrowRight') { e.preventDefault(); show(i + 1); }
        else if (e.key === 'ArrowLeft') { e.preventDefault(); show(i - 1); }
      });
    }

    /* ---------- add to cart ---------- */
    bindAtc() {
      if (!this.form) return;
      var self = this;
      this.form.addEventListener('submit', function (e) {
        if (!window.fetch) return; // let the native POST happen
        e.preventDefault();
        self.addToCart();
      });
    }

    addToCart() {
      var self = this;
      var btn = this.atcBtn;
      var original = this.atcText ? this.atcText.textContent : '';
      if (btn) { btn.disabled = true; btn.classList.add('is-loading'); }
      if (this.atcText) this.atcText.textContent = 'Adding…';

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        body: new FormData(this.form)
      })
        .then(function (r) { if (!r.ok) throw new Error('add failed'); return r.json(); })
        .then(function () { return fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(function (r) { return r.json(); }); })
        .then(function (cart) {
          self.updateCartCount(cart.item_count);
          document.dispatchEvent(new CustomEvent('tefco:cart:added', { bubbles: true, detail: { cart: cart } }));
          if (btn) { btn.disabled = false; btn.classList.remove('is-loading'); }
          if (self.atcText) self.atcText.textContent = 'Added ✓';
          setTimeout(function () { if (self.atcText) self.atcText.textContent = original || 'Add to Cart'; }, 2000);
        })
        .catch(function () {
          // graceful fallback: native navigation to the cart
          if (btn) { btn.disabled = false; btn.classList.remove('is-loading'); }
          if (self.atcText) self.atcText.textContent = original || 'Add to Cart';
          self.form.submit();
        });
    }

    updateCartCount(count) {
      var sel = ['[data-cart-count]', '.cart-count', '.cart-count-bubble', '.header__cart-count', '.count-cart', '#cart-icon-bubble .cart-count-bubble span'];
      document.querySelectorAll(sel.join(',')).forEach(function (el) {
        el.textContent = count;
        el.classList.remove('hidden', 'is-hidden');
      });
    }

    /* ---------- quote link ---------- */
    syncQuote() {
      if (!this.quote) return;
      var params = new URLSearchParams();
      params.set('product', this.getAttribute('data-product-title') || document.title);
      if (this.idInput && this.idInput.value) params.set('variant', this.idInput.value);
      if (this.qtyInput && this.qtyInput.value) params.set('qty', this.qtyInput.value);
      this.quote.setAttribute('href', this.quoteBase + '?' + params.toString());
    }
  }

  if (!customElements.get('tefco-product')) customElements.define('tefco-product', TefcoProduct);
})();
